import Route from '@ember/routing/route'
import type DateFilterService from 'district-ui-client/services/date-filter'
import type Store from '@ember-data/store'
import type RouterService from '@ember/routing/router-service'
import type Transition from '@ember/routing/transition'
import { service } from '@ember/service'
import type ReportingUiScopeController from 'district-ui-client/reporting/ui-scope/controller'
import type SubscriptionTypeRoute from 'district-ui-client/subscription-type/route'
import type SessionService from 'district-ui-client/services/session'
import { UIScope, validUiScopesFor } from 'district-ui-client/domain/ui-scope'
import { isMultiSchoolReport, isReportPath } from 'district-ui-client/services/report-registry'
import type District from 'district-ui-client/models/district'
import type School from 'district-ui-client/models/school'
import { hasId } from 'district-ui-client/utils/type-guards'

function schoolInScope(schoolId: string, uiScope: UIScope, validScopes: UIScope[]): boolean {
  if (uiScope.scope === 'district') {
    // either school is present in districts' subscopes, or if displaying whole district, school is within valid scopes
    // valid scopes take into account schools' subscription
    if (uiScope.subScopes.length) {
      return uiScope.subScopes.some(({ scope, id }) => scope === 'school' && id === schoolId)
    }
    return validScopes.some(({ scope, id }) => scope === 'school' && id === schoolId)
  }
  return uiScope.scope === 'school' && uiScope.id === schoolId
}

type DistrictWithId = District & { id: string }
type SchoolWithId = School & { id: string }

interface Params extends Record<string, unknown> {
  // dynamic segments in url
  ui_scope_name: UIScope['scope']
  ui_scope_id: UIScope['id']
  // query param in url
  schoolIds?: string[]
}

export class UiScopeRoute extends Route {
  @service router!: RouterService

  @service dateFilter!: DateFilterService

  @service store!: Store

  @service session!: SessionService

  queryParams = {
    dateRange: {
      refreshModel: true,
      replace: true,
    },
    schoolIds: {
      refreshModel: true,
      replace: true,
    },
  }

  beforeModel(transition: Transition): void {
    // set the dateFilter service dateRange based on the transition, so that it's available for the model
    // the controller (& transition) will be updated in setupController. Doing a transition here seems to cause bugs.
    const dateRange = transition.to?.queryParams.dateRange
    if (dateRange && typeof dateRange === 'string') {
      this.dateFilter.dateRange = dateRange
    } else if (isReportPath(transition.to?.name) && isMultiSchoolReport(transition.to.name)) {
      this.dateFilter.dateRange = this.dateFilter.toDateRange(this.dateFilter.thisAcademicPeriod)
    } else {
      this.dateFilter.dateRange = this.dateFilter.toDateRange(this.dateFilter.thisMonth)
    }

    const { subscriptionType } = this.modelFor('subscription-type') as ModelFor<SubscriptionTypeRoute>
    const {
      ui_scope_id: uiScopeId,
      ui_scope_name: uiScopeName,
      schoolIds,
    } = this.paramsFor('reporting.ui-scope') as Params

    // Determine the valid scopes that can be visited, and check the one we're visiting is valid
    const validUiScopes = validUiScopesFor(
      this.session.currentDistrict,
      this.store.peekAll<School>('school'),
      subscriptionType,
    )
    const isValidScope = validUiScopes.some(({ scope, id }) => scope === uiScopeName && id === uiScopeId)
    const isValidSubscope =
      !schoolIds?.length ||
      (uiScopeName === 'district' &&
        schoolIds.every((schoolId) => validUiScopes.some(({ scope, id }) => scope === 'school' && id === schoolId)))

    if (!isValidScope || !isValidSubscope) {
      // ember 4.12 routing bug causes the transition to 'subscription-type.error404' to fail here. This will do for now
      void this.router.replaceWith(`/${subscriptionType}/error404`)
      return
    }
  }

  model(params: Params) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelFor<SubscriptionTypeRoute>
    const { ui_scope_name: uiScopeName, ui_scope_id: uiScopeId, schoolIds } = params

    const uiSubScopes = schoolIds?.map((schoolId) => new UIScope('school', schoolId)) ?? []

    const uiScope = new UIScope(uiScopeName, uiScopeId, uiSubScopes)

    let districts: DistrictWithId[] = []
    let schools: SchoolWithId[] = []
    if (uiScope.scope === 'district') {
      const peekedRecord = this.store.peekRecord<District>('district', uiScope.id)
      districts = peekedRecord && hasId(peekedRecord) ? [peekedRecord] : []

      const allSchools = this.store.peekAll<School>('school')
      const validUiScopes = validUiScopesFor(this.session.currentDistrict, allSchools, subscriptionType)
      schools = allSchools.filter(hasId).filter((school) => schoolInScope(school.id, uiScope, validUiScopes))
    } else if (uiScope.scope === 'school') {
      districts = []
      const peekedRecord = this.store.peekRecord<School>('school', uiScope.id)
      schools = peekedRecord && hasId(peekedRecord) ? [peekedRecord] : []
    }

    const scopedData = { districts, schools }

    return {
      uiScope,
      period: this.dateFilter.period,
      scopedData,
    }
  }

  setupController(controller: ReportingUiScopeController, model: unknown, transition: Transition) {
    super.setupController(controller, model, transition)
    controller.drawer = true

    controller.dateRange = this.dateFilter.dateRange
  }
}

export default UiScopeRoute
